import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Copy, Film, Megaphone, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { OutOfTokensAlert } from '@/components/billing/OutOfTokens'
import {
    aiErrorMessage,
    formatTokens,
    generateSocialPosts,
    generateVideoBrief,
    getAIOverview,
    isInsufficientTokens,
    type AIOverview,
    type SocialPlatform,
    type SocialPost,
    type VideoBrief,
} from '@/lib/ai'

/**
 * Marketing for one listing: platform-specific social copy and a
 * scene-by-scene video brief, both written only from the listing's own facts.
 *
 * Neither publishes anything. Social accounts are not connected from Hero and
 * video rendering is not wired up, so the panel says so plainly rather than
 * implying a post went out.
 */
const PLATFORM_LABELS: Record<SocialPlatform, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    threads: 'Threads',
    linkedin: 'LinkedIn',
}

const ALL_PLATFORMS: SocialPlatform[] = ['facebook', 'instagram', 'threads', 'linkedin']

interface AIMarketingPanelProps {
    listingId: number
}

const AIMarketingPanel = ({ listingId }: AIMarketingPanelProps) => {
    const [open, setOpen] = useState(false)
    const [tab, setTab] = useState<'social' | 'video'>('social')
    const [overview, setOverview] = useState<AIOverview | null>(null)
    const [platforms, setPlatforms] = useState<SocialPlatform[]>(['facebook', 'instagram'])
    const [posts, setPosts] = useState<SocialPost[]>([])
    const [publishingNote, setPublishingNote] = useState('')
    const [brief, setBrief] = useState<VideoBrief | null>(null)
    const [videoNote, setVideoNote] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<{ message: string; insufficient: boolean } | null>(null)
    const [balanceAfter, setBalanceAfter] = useState<number | null>(null)
    const [copiedKey, setCopiedKey] = useState<string>('')

    useEffect(() => {
        if (!open || overview) return
        getAIOverview()
            .then(setOverview)
            .catch(() => setOverview(null))
    }, [open, overview])

    const cost = (key: string) => overview?.operations.find((op) => op.operationKey === key)?.tokenCost ?? null
    const available = balanceAfter ?? overview?.balance?.availableTokens ?? null
    const notConfigured = overview?.status && !overview.status.openai.configured

    const togglePlatform = (platform: SocialPlatform) => {
        setPlatforms((current) => (current.includes(platform) ? current.filter((p) => p !== platform) : [...current, platform]))
    }

    const runSocial = async () => {
        if (!platforms.length) {
            toast.error('Choose at least one platform.')
            return
        }
        setBusy(true)
        setError(null)
        try {
            const data = await generateSocialPosts(listingId, { platforms })
            setPosts(data.posts || [])
            setPublishingNote(data.publishing?.message || '')
            setBalanceAfter(data.usage?.balanceAfter ?? null)
        } catch (err) {
            setError({ message: aiErrorMessage(err), insufficient: isInsufficientTokens(err) })
        } finally {
            setBusy(false)
        }
    }

    const runVideo = async () => {
        setBusy(true)
        setError(null)
        try {
            const data = await generateVideoBrief(listingId, { durationSeconds: 20, aspectRatio: '9:16' })
            setBrief(data.brief)
            setVideoNote(data.generation?.message || '')
            setBalanceAfter(data.usage?.balanceAfter ?? null)
        } catch (err) {
            setError({ message: aiErrorMessage(err), insufficient: isInsufficientTokens(err) })
        } finally {
            setBusy(false)
        }
    }

    const copy = async (key: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedKey(key)
            setTimeout(() => setCopiedKey(''), 2000)
        } catch {
            toast.error('Could not copy to the clipboard.')
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button type='button' variant='outline' size='sm'>
                    <Megaphone className='h-3.5 w-3.5' /> Generate Marketing
                </Button>
            </DialogTrigger>
            <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
                <DialogHeader>
                    <DialogTitle>Marketing for this listing</DialogTitle>
                    <DialogDescription>
                        Copy is written from this listing&rsquo;s own details. Nothing is posted or rendered from here.
                    </DialogDescription>
                </DialogHeader>

                <nav className='flex gap-1 border-b'>
                    {(['social', 'video'] as const).map((value) => (
                        <button
                            key={value}
                            type='button'
                            onClick={() => setTab(value)}
                            className={`px-4 py-2 text-sm cursor-pointer ${tab === value ? 'font-semibold text-black border-b-2 border-black' : 'text-muted-foreground'}`}>
                            {value === 'social' ? 'Social posts' : 'Video brief'}
                        </button>
                    ))}
                </nav>

                {notConfigured && (
                    <Alert className='border-amber-300 bg-amber-50'>
                        <AlertDescription>{overview?.status.openai.message || 'AI is not configured on this environment yet.'}</AlertDescription>
                    </Alert>
                )}

                {error &&
                    (error.insufficient ? (
                        <OutOfTokensAlert message={error.message} />
                    ) : (
                        <Alert className='border-red-300 bg-red-50'>
                            <AlertDescription>{error.message}</AlertDescription>
                        </Alert>
                    ))}

                <p className='text-xs text-muted-foreground'>
                    {tab === 'social' && cost('social_post') !== null && <>Costs {formatTokens(cost('social_post'))} tokens. </>}
                    {tab === 'video' && cost('video_prompt') !== null && <>Costs {formatTokens(cost('video_prompt'))} tokens. </>}
                    {available !== null && <>You have {formatTokens(available)} tokens.</>}
                </p>

                {tab === 'social' ? (
                    <div className='space-y-4'>
                        <div className='flex flex-wrap gap-4'>
                            {ALL_PLATFORMS.map((platform) => (
                                <div key={platform} className='flex items-center gap-2'>
                                    <Checkbox
                                        id={`platform-${platform}`}
                                        checked={platforms.includes(platform)}
                                        onCheckedChange={() => togglePlatform(platform)} />
                                    <Label htmlFor={`platform-${platform}`} className='cursor-pointer'>{PLATFORM_LABELS[platform]}</Label>
                                </div>
                            ))}
                        </div>

                        {busy ? (
                            <div className='flex items-center justify-center gap-2 py-8'>
                                <Spinner variant='circle' className='h-4 w-4' />
                                <span className='text-sm text-muted-foreground'>Writing…</span>
                            </div>
                        ) : posts.length > 0 ? (
                            <div className='space-y-3'>
                                {posts.map((post) => (
                                    <div key={post.platform} className='rounded-xl border p-4'>
                                        <div className='flex items-center justify-between gap-2'>
                                            <Badge variant='secondary'>{PLATFORM_LABELS[post.platform]}</Badge>
                                            <Button
                                                type='button'
                                                variant='ghost'
                                                size='sm'

                                                onClick={() => copy(post.platform, `${post.text}\n\n${post.hashtags.join(' ')}`.trim())}>
                                                <Copy className='h-3.5 w-3.5' /> {copiedKey === post.platform ? 'Copied' : 'Copy'}
                                            </Button>
                                        </div>
                                        <p className='text-sm whitespace-pre-wrap mt-2'>{post.text}</p>
                                        {post.hashtags.length > 0 && (
                                            <p className='text-xs text-blue-700 mt-2'>{post.hashtags.join(' ')}</p>
                                        )}
                                        {post.callToAction && <p className='text-xs text-muted-foreground mt-1'>{post.callToAction}</p>}
                                    </div>
                                ))}
                                {publishingNote && <p className='text-xs text-muted-foreground'>{publishingNote}</p>}
                            </div>
                        ) : null}

                        <Button type='button' disabled={busy || Boolean(notConfigured)} onClick={runSocial}>
                            {posts.length > 0 ? <RotateCcw className='h-4 w-4' /> : <Megaphone className='h-4 w-4' />}
                            {posts.length > 0 ? 'Regenerate' : 'Generate posts'}
                        </Button>
                    </div>
                ) : (
                    <div className='space-y-4'>
                        {busy ? (
                            <div className='flex items-center justify-center gap-2 py-8'>
                                <Spinner variant='circle' className='h-4 w-4' />
                                <span className='text-sm text-muted-foreground'>Planning…</span>
                            </div>
                        ) : brief ? (
                            <div className='space-y-3'>
                                <div>
                                    <p className='font-semibold'>{brief.title}</p>
                                    <p className='text-xs text-muted-foreground'>{brief.durationSeconds}s · {brief.aspectRatio}</p>
                                </div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className='w-10'>#</TableHead>
                                            <TableHead>Scene</TableHead>
                                            <TableHead>Visual</TableHead>
                                            <TableHead>Voiceover</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {brief.scenes.map((scene) => (
                                            <TableRow key={scene.order}>
                                                <TableCell>{scene.order}</TableCell>
                                                <TableCell className='whitespace-nowrap'>{scene.label} <span className='text-xs text-muted-foreground'>({scene.durationSeconds}s)</span></TableCell>
                                                <TableCell className='text-xs'>{scene.visual}</TableCell>
                                                <TableCell className='text-xs'>{scene.voiceover}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <div className='rounded-xl border p-4'>
                                    <div className='flex items-center justify-between gap-2'>
                                        <Label className='text-xs uppercase text-muted-foreground'>Provider prompt</Label>
                                        <Button type='button' variant='ghost' size='sm' onClick={() => copy('prompt', brief.providerPrompt)}>
                                            <Copy className='h-3.5 w-3.5' /> {copiedKey === 'prompt' ? 'Copied' : 'Copy'}
                                        </Button>
                                    </div>
                                    <p className='text-xs whitespace-pre-wrap mt-1'>{brief.providerPrompt}</p>
                                </div>
                                {videoNote && <p className='text-xs text-muted-foreground'>{videoNote}</p>}
                            </div>
                        ) : null}

                        <Button type='button' disabled={busy || Boolean(notConfigured)} onClick={runVideo}>
                            {brief ? <RotateCcw className='h-4 w-4' /> : <Film className='h-4 w-4' />}
                            {brief ? 'Regenerate' : 'Generate video brief'}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default AIMarketingPanel
